import type { GVector } from '../physics/index.ts';

export type SimulatedIntensityLevel = 'comfortable' | 'noticeable' | 'high' | 'extreme';

export interface SimulatedIntensitySummary {
  level: SimulatedIntensityLevel;
  label: string;
  message: string;
}

const SUMMARIES: Record<SimulatedIntensityLevel, SimulatedIntensitySummary> = {
  comfortable: {
    level: 'comfortable',
    label: 'Comfortable',
    message: 'Forces stayed in the initial comfort band.',
  },
  noticeable: {
    level: 'noticeable',
    label: 'Noticeable',
    message: 'A few stronger moments—check the peak section.',
  },
  high: {
    level: 'high',
    label: 'High',
    message: 'Strong simulated forces—reshape the peak section.',
  },
  extreme: {
    level: 'extreme',
    label: 'Extreme',
    message: 'Extreme simulated forces—revise this track before sharing it.',
  },
};

export function classifySimulatedIntensity(vector: GVector): SimulatedIntensitySummary {
  const lateral = Math.abs(vector.lateral);
  const longitudinal = Math.abs(vector.longitudinal);
  if (vector.vertical > 8 || vector.vertical < -3 || lateral > 4 || longitudinal > 4)
    return SUMMARIES.extreme;
  if (vector.vertical > 5 || vector.vertical < -1 || lateral > 1.25 || longitudinal > 1.25)
    return SUMMARIES.high;
  if (vector.vertical > 4 || vector.vertical < -0.5 || lateral > 0.75 || longitudinal > 0.75)
    return SUMMARIES.noticeable;
  return SUMMARIES.comfortable;
}

export function simulatedIntensityScore(vector: GVector): number {
  const verticalScore =
    vector.vertical >= 0 ? Math.abs(vector.vertical) / 4 : Math.abs(vector.vertical) / 0.5;
  return Math.max(
    verticalScore,
    Math.abs(vector.lateral) / 0.75,
    Math.abs(vector.longitudinal) / 0.75,
  );
}
